import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatDateTime } from "@/lib/format";

type RecurringInvoiceSchedule = components["schemas"]["RecurringInvoiceScheduleResponse"];
type RecurringScheduleStatus = NonNullable<components["schemas"]["RecurringScheduleStatus"]>;

interface RecurringInvoiceSchedulesTableProps {
  organisationId?: string;
  customerId?: string;
  /** Keep the Organisation column visible even when filtered by it. */
  showAllColumns?: boolean;
}

const statusFilterOptions: { value: RecurringScheduleStatus | "any"; label: string }[] = [
  { value: "any", label: "All statuses" },
  { value: "Active", label: "Active" },
  { value: "Paused", label: "Paused" },
  { value: "Completed", label: "Completed" },
  { value: "Cancelled", label: "Cancelled" },
];

function statusVariant(status: string | undefined): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "Active":
      return "default";
    case "Paused":
      return "secondary";
    case "Cancelled":
      return "destructive";
    case "Completed":
      return "outline";
    default:
      return "outline";
  }
}

function formatOccurrences(
  generated: number | string | undefined,
  total: number | string | null | undefined,
): string {
  const g = generated ?? 0;
  return total == null ? `${g} / ∞` : `${g} / ${total}`;
}

export function RecurringInvoiceSchedulesTable({
  organisationId,
  customerId,
  showAllColumns = false,
}: RecurringInvoiceSchedulesTableProps) {
  const navigate = useNavigate();
  const [status, setStatus] = useState<RecurringScheduleStatus | "any">("any");
  const pagination = useCursorPagination({ initialLimit: 20 });

  const [schedules, setSchedules] = useState<RecurringInvoiceSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const prevFiltersRef = useRef({ status, organisationId, customerId });
  useEffect(() => {
    if (
      prevFiltersRef.current.status !== status ||
      prevFiltersRef.current.organisationId !== organisationId ||
      prevFiltersRef.current.customerId !== customerId
    ) {
      prevFiltersRef.current = { status, organisationId, customerId };
      pagination.reset();
    }
  }, [status, organisationId, customerId, pagination.reset]);

  useEffect(() => {
    let cancelled = false;

    async function fetchSchedules() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/recurring-invoices", {
        params: {
          query: {
            OrganisationId: organisationId,
            CustomerId: customerId,
            Statuses: status === "any" ? undefined : [status],
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load recurring invoice schedules.");
      } else {
        setSchedules(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchSchedules();
    return () => {
      cancelled = true;
    };
  }, [organisationId, customerId, status, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  const showOrganisationColumn = showAllColumns || !organisationId;
  const colSpan = 8 + (showOrganisationColumn ? 1 : 0);

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <Select
          value={status}
          onValueChange={(v) => setStatus(v as RecurringScheduleStatus | "any")}
        >
          <SelectTrigger className="w-44">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {statusFilterOptions.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {loading && <Spinner className="size-5" />}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              {showOrganisationColumn && <TableHead>Organisation</TableHead>}
              <TableHead>Customer</TableHead>
              <TableHead>Cadence</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Occurrences</TableHead>
              <TableHead>Start</TableHead>
              <TableHead>Next run</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {schedules.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={colSpan} className="text-muted-foreground h-24 text-center">
                  No recurring invoice schedules found.
                </TableCell>
              </TableRow>
            ) : (
              schedules.map((schedule) => (
                <TableRow
                  key={schedule.id}
                  onClick={() => navigate(`/recurring-invoices/${schedule.id}`)}
                  className="cursor-pointer"
                >
                  <TableCell>
                    <Badge variant="secondary" className="font-mono text-xs">
                      {schedule.id?.slice(0, 8)}...
                    </Badge>
                  </TableCell>
                  {showOrganisationColumn && (
                    <TableCell>
                      <Link
                        to={`/organisations/${schedule.organisationId}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Badge
                          variant="secondary"
                          className="font-mono text-xs hover:bg-primary/20"
                        >
                          {schedule.organisationId?.slice(0, 8)}...
                        </Badge>
                      </Link>
                    </TableCell>
                  )}
                  <TableCell className="text-sm">
                    <Link
                      to={`/customers/${schedule.customerId}?organisationId=${schedule.organisationId}`}
                      onClick={(e) => e.stopPropagation()}
                      className="hover:underline"
                    >
                      {schedule.customer?.name ?? (
                        <span className="font-mono text-xs text-muted-foreground">
                          {schedule.customerId?.slice(0, 8)}...
                        </span>
                      )}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">{schedule.cadence}</TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(schedule.status)}>{schedule.status}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">
                    {formatOccurrences(schedule.occurrencesGenerated, schedule.totalOccurrences)}
                  </TableCell>
                  <TableCell className="text-sm">{formatDateTime(schedule.startDate)}</TableCell>
                  <TableCell className="text-sm">
                    {schedule.nextRunAt ? (
                      formatDateTime(schedule.nextRunAt)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{formatDateTime(schedule.createdAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}
